import { BaseDAO } from './baseDAO';
import { Injectable } from '@angular/core';
import { ConstantMan } from '../constantMan';
import { ApiHelper } from '../apiHelper';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import { Captain } from 'anchor-shared/lib/model/entity/captain';
import { CustomerCaptain } from 'anchor-shared/lib/model/interface/customerInterfaces';
import { CaptainCrewMemberMap } from 'anchor-shared/lib/model/entity/captainCrewMemberMap';

@Injectable()
export class CaptainDAO extends BaseDAO<Captain> {
  static PATHS = {
    ADD_CAPTAIN_TO_USER: '/addNewCaptainToUser',
    UPDATE_USER_CAPTAIN: '/updateUserCaptain',
    UPDATE_USER_CAPTAIN_WATER_BODIES: '/updateUserCaptainWaterBodies',
    UPDATE_USER_CAPTAIN_WATER_BODIES_ADMIN: '/admin/water-body-sync/',
    GET_SIGNED_FILE: '/get-signed-file',
    GET_SIGNED_FILE_ADMIN: '/get-signed-file-admin',
  };

  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.CAPTAIN);
  }

  public setCaptainCrewMemberMap(
    captainId: number,
    crewIds: number[],
  ): Observable<CaptainCrewMemberMap[]> {
    const url = `${this.apiHelper.getServiceName()}${this.getResource()}/${captainId}/set-captain-crew-members`;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<CaptainCrewMemberMap[]>(
      url,
      method,
      null,
      { crewIds: crewIds },
      headers,
    );
  }

  public updateFromPartial(
    captainId: number,
    updates: Partial<Captain>,
  ): Observable<CaptainCrewMemberMap[]> {
    const url = `${this.apiHelper.getServiceName()}${this.getResource()}/partial/${captainId}`;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<CaptainCrewMemberMap[]>(
      url,
      method,
      null,
      updates,
      headers,
    );
  }

  public loadCaptainsForAdminGrid(): Observable<Captain[]> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/grid/admin';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<Captain[]> = this.apiHelper.apiCall<
      Captain[]
    >(url, method, null, {}, headers);

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captains: Captain[]) => {
          observer.next(captains);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public loadActiveCaptains(): Observable<Captain[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/active/vessel-edit';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Captain[]>(url, method, null, {}, headers);
  }

  public loadCaptainsByIds(ids: number[]): Observable<Captain[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/vessel-preferred-list';
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      ids: ids,
    };

    return this.apiHelper.apiCall<Captain[]>(url, method, null, data, headers);
  }

  public loadCustomerCaptainsForVessel(
    ids: number[],
  ): Observable<CustomerCaptain[]> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/customer/vessel-preferred-list';
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      ids: ids,
    };

    return this.apiHelper.apiCall<CustomerCaptain[]>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public loadCaptainsForHire(): Observable<Captain[]> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/for-hire';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Captain[]>(url, method, null, null, headers);
  }

  public loadCaptainForHireById(captainId: number): Observable<Captain> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/for-hire/' +
      captainId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Captain>(url, method, null, null, headers);
  }

  public getByCaptainId(captainId: any): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/' + captainId;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, {}, headers);
  }

  adminActiveCaptainList(): Observable<Captain[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/admin-active-captain-list';
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, {}, headers);
  }

  public findCaptainByWaterBodyId(
    waterBodyId: number,
    limit?: number,
  ): Observable<Captain[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/find-captain-by-water-body-id/' +
      waterBodyId;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall(url, method, null, null, headers);
  }

  public getActiveCaptains(): Observable<Captain[]> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/';
    let method: string = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Captain[]>(url, method, null, null, headers);
  }

  public getCaptainsByIdArray(): Observable<Captain[]> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/';
    let method: string = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Captain[]>(url, method, null, null, headers);
  }

  public addNewCaptainToUser(): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.ADD_CAPTAIN_TO_USER;
    let method: string = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<Captain> = this.apiHelper.apiCall<Captain>(
      url,
      method,
      null,
      {},
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captain: Captain) => {
          observer.next(captain);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public addNewCaptainToExistingUser(userId: number): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.ADD_CAPTAIN_TO_USER +
      '/' +
      userId;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let httpRequest: Observable<Captain> = this.apiHelper.apiCall<Captain>(
      url,
      method,
      null,
      null,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captain: Captain) => {
          observer.next(captain);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateUserCaptain(captain: Captain): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.UPDATE_USER_CAPTAIN;
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let captainData: Object = captain;

    let httpRequest: Observable<Captain> = this.apiHelper.apiCall<Captain>(
      url,
      method,
      null,
      captainData,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captain: Captain) => {
          console.log(captain);
          observer.next(captain);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateCaptain(captain: Captain): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/' + captain.id;
    let method: string = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let captainData: Object = captain;

    let httpRequest: Observable<Captain> = this.apiHelper.apiCall<Captain>(
      url,
      method,
      null,
      captainData,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captain: Captain) => {
          console.log(captain);
          observer.next(captain);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateUserCaptainWaterBodiesAdmin(
    waterBodyIds: Array<number>,
    id: number,
  ): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.UPDATE_USER_CAPTAIN_WATER_BODIES_ADMIN +
      id;
    let method: string = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      waterBodyIds: waterBodyIds,
    };
    let httpRequest: Observable<Captain> = this.apiHelper.apiCall<Captain>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captain: Captain) => {
          console.log(captain);
          observer.next(captain);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateUserCaptainWaterBodies(
    waterBodyIds: Array<number>,
  ): Observable<Captain> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.UPDATE_USER_CAPTAIN_WATER_BODIES;
    let method: string = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      waterBodyIds: waterBodyIds,
    };
    let httpRequest: Observable<Captain> = this.apiHelper.apiCall<Captain>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (captain: Captain) => {
          console.log(captain);
          observer.next(captain);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  getSignedUrl(): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.GET_SIGNED_FILE;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<any>(url, method, null, null, headers);
  }

  getSignedUrlAdmin(fileKey: string): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      CaptainDAO.PATHS.GET_SIGNED_FILE_ADMIN;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      key: fileKey,
    };
    return this.apiHelper.apiCall<any>(url, method, null, data, headers);
  }
}
